import { createAsyncThunk, createSlice, Draft, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import {
  createDefaultFetch,
  createFetchedSuccess,
  createFetchedWithError,
  createFetchInProgress,
  IFetchStatus,
} from 'types/rest/IFetchStatus';
import { convertSerializedErrorToError } from './convertSerializedErrorToError';
import { IFetchKeyedDataState } from './IFetchKeyedDataState';

export const createKeyedDataAsyncSlice = <Query, Data>(
  name: string,
  action: (x: Query) => Promise<Data>,
  key: (x: Query) => string
) => {
  const asyncAction = createAsyncThunk(`${name}/fetchData`, action);
  const initialState = {} as IFetchKeyedDataState<Query, Data>;
  const slice = createSlice<IFetchKeyedDataState<Query, Data>, SliceCaseReducers<IFetchKeyedDataState<Query, Data>>>({
    name,
    initialState,
    reducers: {
      reset: () => ({}),
      resetByKey: (state, action) => {
        (state as any)[action.payload] = undefined;
      },
      setByKey: (state, action: PayloadAction<{ key: string; value: Data }>) => {
        (state as any)[action.payload.key] = {
          data: action.payload.value,
          fetchStatus: createFetchedSuccess(),
          updateFetchStatus: createDefaultFetch(),
        };
      },
    },
    extraReducers: (builder) =>
      builder
        .addCase(asyncAction.pending, (state, action) => {
          const keyValue = key(action.meta.arg);

          const myValue = state[keyValue];

          state[keyValue] = {
            ...myValue,
            query: action.meta.arg as Draft<Query>,
            fetchStatus: !myValue?.data ? createFetchInProgress() : myValue?.fetchStatus,
            updateFetchStatus: !!myValue?.data ? createFetchInProgress() : myValue?.updateFetchStatus,
          };
        })
        .addCase(asyncAction.fulfilled, (state, action) => {
          const keyValue = key(action.meta.arg);
          const newState = {
            ...state[keyValue],
            data: action.payload as Draft<Data>,
          };
          if (!state[keyValue]?.data) {
            newState.fetchError = undefined;
            newState.fetchStatus = createFetchedSuccess();
          } else {
            newState.updateFetchError = undefined;
            newState.updateFetchStatus = createFetchedSuccess();
          }
          state[keyValue] = newState;
        })
        .addCase(asyncAction.rejected, (state, action) => {
          const keyValue = key(action.meta.arg);
          const newError = convertSerializedErrorToError(action.error);
          const newStatus = createFetchedWithError();
          const newState = {
            ...state[keyValue],
          };
          if (!state[keyValue]?.data) {
            newState.fetchError = newError;
            newState.fetchStatus = newStatus;
          } else {
            newState.updateFetchError = newError;
            newState.updateFetchStatus = newStatus;
          }
          state[keyValue] = newState;
        }),
  });
  return { slice, asyncAction };
};

export const getKey = <Query, Data>(
  x: string | undefined,
  allData: IFetchKeyedDataState<Query, Data>
): {
  data: Data | undefined;
  fetchStatus: IFetchStatus;
  fetchError: Error | undefined;
  updateFetchStatus: IFetchStatus | undefined;
  updateFetchError: Error | undefined;
  query: Query | undefined;
} => {
  if (x && allData[x]) {
    return {
      data: allData[x].data,
      fetchStatus: allData[x].fetchStatus,
      fetchError: allData[x].fetchError,
      updateFetchStatus: allData[x].updateFetchStatus,
      updateFetchError: allData[x].updateFetchError,
      query: allData[x].query,
    };
  }
  return {
    data: undefined,
    fetchStatus: createDefaultFetch(),
    fetchError: undefined,
    updateFetchStatus: createDefaultFetch(),
    updateFetchError: undefined,
    query: undefined,
  };
};

export const generateSliceKey = (...args: Array<string | undefined>) => args.join('-');

// defaultData is kept undefined for this PR so that I wouldn't have to add default data to every keyed slice.
// perhaps in the future it should be mandatory so that there's always default data.
export const useKeyToSelect = <T, Q, D>(
  key: string,
  actionToSelect: (x: T) => IFetchKeyedDataState<Q, D>,
  defaultData?: D
) => {
  const slice = useSelector(actionToSelect);
  return getKey(key, slice) || defaultData;
};
